import React, { useState, useEffect } from 'react';
import { pageApi, PageWithComponents, DashboardPageCreate, DashboardComponent } from '../api/pages';
import { componentApi } from '../api/components';

interface DashboardPagesProps {
  userInfo?: any;
}

const DashboardPages: React.FC<DashboardPagesProps> = ({ userInfo }) => {
  const [pages, setPages] = useState<PageWithComponents[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showComponentModal, setShowComponentModal] = useState(false);
  const [showComponentEditModal, setShowComponentEditModal] = useState(false);
  const [selectedPage, setSelectedPage] = useState<PageWithComponents | null>(null);
  const [selectedComponent, setSelectedComponent] = useState<DashboardComponent | null>(null);
  const [expandedPages, setExpandedPages] = useState<Set<string>>(new Set());
  const [formData, setFormData] = useState<DashboardPageCreate>({
    page_id: '',
    page_name: '',
    page_path: '',
    page_icon: '',
    description: '',
    category: '',
    is_active: true
  });

  const [componentFormData, setComponentFormData] = useState({
    component_id: '',
    component_name: '',
    description: '',
    component_type: '',
    page_id: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await pageApi.getPagesWithComponents();
      setPages(data);
    } catch (error) {
      console.error('Error loading dashboard pages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await pageApi.create(formData);
      setShowCreateModal(false);
      setFormData({
        page_id: '',
        page_name: '',
        page_path: '',
        page_icon: '',
        description: '',
        category: '',
        is_active: true
      });
      loadData();
    } catch (error) {
      console.error('Error creating dashboard page:', error);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPage) return;
    
    try {
      await pageApi.update(selectedPage.page_id, formData);
      setShowEditModal(false);
      setFormData({
        page_id: '',
        page_name: '',
        page_path: '',
        page_icon: '',
        description: '',
        category: '',
        is_active: true
      });
      setSelectedPage(null);
      loadData();
    } catch (error) {
      console.error('Error updating dashboard page:', error);
    }
  };

  const handleDelete = async (page_id: string) => {
    if (window.confirm('Are you sure you want to delete this dashboard page?')) {
      try {
        await pageApi.delete(page_id);
        loadData();
      } catch (error) {
        console.error('Error deleting dashboard page:', error);
      }
    }
  };

  const openEditModal = (page: PageWithComponents) => {
    setSelectedPage(page);
    setFormData({
      page_id: page.page_id,
      page_name: page.page_name,
      page_path: page.page_path,
      page_icon: page.page_icon,
      description: page.description || '',
      category: page.category || '',
      is_active: page.is_active
    });
    setShowEditModal(true);
  };

  const togglePageExpansion = (pageId: string) => {
    const newExpanded = new Set(expandedPages);
    if (newExpanded.has(pageId)) {
      newExpanded.delete(pageId);
    } else {
      newExpanded.add(pageId);
    }
    setExpandedPages(newExpanded);
  };

  const openComponentModal = (page: PageWithComponents) => {
    setSelectedPage(page);
    setComponentFormData({
      component_id: '',
      component_name: '',
      page_id: page.page_id,
      description: '',
      component_type: ''
    });
    setShowComponentModal(true);
  };

  const openComponentEditModal = (component: DashboardComponent) => {
    setSelectedComponent(component);
    setComponentFormData({
      component_id: component.component_id,
      component_name: component.component_name,
      page_id: component.page_id,
      description: component.description || '',
      component_type: component.component_type || ''
    });
    setShowComponentEditModal(true);
  };

  const handleComponentCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await componentApi.create(componentFormData);
      setShowComponentModal(false);
      setComponentFormData({
        component_id: '',
        component_name: '',
        page_id: '',
        description: '',
        component_type: ''
      });
      loadData();
    } catch (error) {
      console.error('Error creating component:', error);
    }
  };

  const handleComponentUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedComponent) return;
    
    try {
      await componentApi.update(selectedComponent.component_id, componentFormData);
      setShowComponentEditModal(false);
      setComponentFormData({
        component_id: '',
        component_name: '',
        page_id: '',
        description: '',
        component_type: ''
      });
      setSelectedComponent(null);
      loadData();
    } catch (error) {
      console.error('Error updating component:', error);
    }
  };

  const handleComponentDelete = async (componentId: string) => {
    if (window.confirm('Are you sure you want to disable this component?')) {
      try {
        await componentApi.disable(componentId);
        loadData();
      } catch (error) {
        console.error('Error disabling component:', error);
      }
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading dashboard pages...</div>;
  }

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-bold text-gray-900">Dashboard Pages</h1>
          <p className="mt-2 text-gray-600">
            Manage dashboard pages and their components
          </p>
        </div>
        {userInfo?.is_admin && (
          <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Add Page
            </button>
          </div>
        )}
      </div>

      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Page
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Path
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Components
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    {userInfo?.is_admin && (
                      <th className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pages.map((page) => (
                    <React.Fragment key={page.id}>
                      {/* Page Row */}
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <button
                              onClick={() => togglePageExpansion(page.page_id)}
                              className="mr-2 text-gray-400 hover:text-gray-600"
                            >
                              {expandedPages.has(page.page_id) ? '▼' : '▶'}
                            </button>
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                <span className="text-sm font-medium text-gray-700">
                                  {page.page_icon}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {page.page_name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {page.page_id}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {page.page_path}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {page.category || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center space-x-2">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {page.components.length} components
                            </span>
                            {userInfo?.is_admin && (
                              <button
                                onClick={() => openComponentModal(page)}
                                className="text-xs text-blue-600 hover:text-blue-800"
                              >
                                + Add
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            page.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {page.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(page.created_at).toLocaleDateString()}
                        </td>
                        {userInfo?.is_admin && (
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => openEditModal(page)}
                                className="text-indigo-600 hover:text-indigo-900"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(page.page_id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                      
                      {/* Components Row */}
                      {expandedPages.has(page.page_id) && (
                        <tr className="bg-gray-50">
                          <td colSpan={7} className="px-6 py-4">
                            <div className="ml-8">
                              <h4 className="text-sm font-medium text-gray-900 mb-3">
                                Components ({page.components.length})
                              </h4>
                              {page.components.length > 0 ? (
                                <div className="space-y-2">
                                  {page.components.map((component) => (
                                    <div key={component.id} className="flex items-center justify-between bg-white p-3 rounded border">
                                      <div className="flex-1">
                                        <div className="flex items-center space-x-3">
                                          <div className="text-sm font-medium text-gray-900">
                                            {component.component_name}
                                          </div>
                                          <span className="text-xs text-gray-500">
                                            ({component.component_id})
                                          </span>
                                          {component.component_type && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                              {component.component_type}
                                            </span>
                                          )}
                                          <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                                            component.is_active 
                                              ? 'bg-green-100 text-green-800' 
                                              : 'bg-red-100 text-red-800'
                                          }`}>
                                            {component.is_active ? 'Active' : 'Inactive'}
                                          </span>
                                        </div>
                                        {component.description && (
                                          <div className="text-xs text-gray-500 mt-1">
                                            {component.description}
                                          </div>
                                        )}
                                      </div>
                                      {userInfo?.is_admin && (
                                        <div className="flex space-x-2">
                                          <button
                                            onClick={() => openComponentEditModal(component)}
                                            className="text-xs text-indigo-600 hover:text-indigo-900"
                                          >
                                            Edit
                                          </button>
                                          <button
                                            onClick={() => handleComponentDelete(component.component_id)}
                                            className="text-xs text-red-600 hover:text-red-900"
                                          >
                                            Delete
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-sm text-gray-500 italic">
                                  No components yet. Click "Add" to create one.
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create Dashboard Page</h3>
              <form onSubmit={handleCreate}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Page ID
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.page_id}
                    onChange={(e) => setFormData({ ...formData, page_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Page Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.page_name}
                    onChange={(e) => setFormData({ ...formData, page_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Page Path
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.page_path}
                    onChange={(e) => setFormData({ ...formData, page_path: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Page Icon
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.page_icon}
                    onChange={(e) => setFormData({ ...formData, page_icon: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 📊, 🏠, ⚙️"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    Create
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedPage && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Dashboard Page</h3>
              <form onSubmit={handleUpdate}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Page ID
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.page_id}
                    onChange={(e) => setFormData({ ...formData, page_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Page Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.page_name}
                    onChange={(e) => setFormData({ ...formData, page_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Page Path
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.page_path}
                    onChange={(e) => setFormData({ ...formData, page_path: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Page Icon
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.page_icon}
                    onChange={(e) => setFormData({ ...formData, page_icon: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 📊, 🏠, ⚙️"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    Update
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Component Create Modal */}
      {showComponentModal && selectedPage && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Add Component to {selectedPage.page_name}
              </h3>
              <form onSubmit={handleComponentCreate}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Component ID
                  </label>
                  <input
                    type="text"
                    required
                    value={componentFormData.component_id}
                    onChange={(e) => setComponentFormData({ ...componentFormData, component_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., store_timing_chart"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Component Name
                  </label>
                  <input
                    type="text"
                    required
                    value={componentFormData.component_name}
                    onChange={(e) => setComponentFormData({ ...componentFormData, component_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Store Timing Chart"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Component Type
                  </label>
                  <select
                    value={componentFormData.component_type}
                    onChange={(e) => setComponentFormData({ ...componentFormData, component_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Type</option>
                    <option value="chart">Chart</option>
                    <option value="table">Table</option>
                    <option value="card">Card</option>
                    <option value="widget">Widget</option>
                    <option value="form">Form</option>
                    <option value="list">List</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={componentFormData.description}
                    onChange={(e) => setComponentFormData({ ...componentFormData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>
                <input
                  type="hidden"
                  value={componentFormData.page_id}
                  readOnly
                />
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowComponentModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    Create Component
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Component Edit Modal */}
      {showComponentEditModal && selectedComponent && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Edit Component: {selectedComponent.component_name}
              </h3>
              <form onSubmit={handleComponentUpdate}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Component ID
                  </label>
                  <input
                    type="text"
                    required
                    value={componentFormData.component_id}
                    onChange={(e) => setComponentFormData({ ...componentFormData, component_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., store_timing_chart"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Component Name
                  </label>
                  <input
                    type="text"
                    required
                    value={componentFormData.component_name}
                    onChange={(e) => setComponentFormData({ ...componentFormData, component_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Store Timing Chart"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Component Type
                  </label>
                  <select
                    value={componentFormData.component_type}
                    onChange={(e) => setComponentFormData({ ...componentFormData, component_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Type</option>
                    <option value="chart">Chart</option>
                    <option value="table">Table</option>
                    <option value="card">Card</option>
                    <option value="widget">Widget</option>
                    <option value="form">Form</option>
                    <option value="list">List</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={componentFormData.description}
                    onChange={(e) => setComponentFormData({ ...componentFormData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>
                <input
                  type="hidden"
                  value={componentFormData.page_id}
                  readOnly
                />
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowComponentEditModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    Update Component
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPages;
